import axios from "axios";
import { useEffect, useState } from "react";
import {
  Eye,
  EyeSlash,
  X,
} from "react-bootstrap-icons";

import { ApiConnectionString } from "../constants/ApiConntection";

export default function PrivacyPinModal({
  show,
  token,
  onClose,
  onUnlocked,
}) {
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [lockedOut, setLockedOut] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const getStatus = async () => {
    if (!token) {
      return;
    }

    try {
      const response = await axios.get(
        `${ApiConnectionString}/api/privacy-pin/status`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = response.data || {};

      setLockedOut(
        data.lockedOut ??
          data.LockedOut ??
          data.isLockedOut ??
          data.IsLockedOut ??
          false
      );
    } catch (error) {
      console.error(
        "Could not load privacy PIN status:",
        error
      );
    }
  };

  useEffect(() => {
    if (show) {
      setPin("");
      setShowPin(false);
      setError("");
      setMessage("");
      setConfirmReset(false);
      setLockedOut(false);

      getStatus();
    }
  }, [show, token]);

  const handlePinChange = (event) => {
    const value = event.target.value
      .replace(/[^0-9]/g, "")
      .slice(0, 4);

    setPin(value);
  };

  const sendResetEmail = async () => {
    try {
      setIsSendingReset(true);
      setError("");
      setMessage("");

      await axios.post(
        `${ApiConnectionString}/api/privacy-pin/forgot`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setConfirmReset(false);

      setMessage(
        "A PIN reset link has been sent to your email. Please check your junk or spam folder if you cannot see it."
      );
    } catch (error) {
      console.error(error);

      setError(
        "The PIN reset email could not be sent. Please try again."
      );
    } finally {
      setIsSendingReset(false);
    }
  };

  const unlock = async () => {
    setError("");
    setMessage("");

    if (!/^\d{4}$/.test(pin)) {
      setError("Please enter your 4-digit PIN.");
      return;
    }

    try {
      setIsLoading(true);

      await axios.post(
        `${ApiConnectionString}/api/privacy-pin/unlock`,
        {
          pin,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setPin("");
      setShowPin(false);
      setError("");
      setMessage("");

      if (onUnlocked) {
        await onUnlocked();
      }

      window.dispatchEvent(
        new Event("privacy-pin-changed")
      );

      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error(error);

      const message =
        typeof error.response?.data === "string"
          ? error.response.data
          : error.response?.data?.message ??
            error.response?.data?.Message ??
            "The PIN could not be verified.";

      setError(message);

      await getStatus();
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    if (isLoading || isSendingReset) {
      return;
    }

    setPin("");
    setShowPin(false);
    setError("");
    setMessage("");
    setConfirmReset(false);

    if (onClose) {
      onClose();
    }
  };

  if (!show) {
    return null;
  }

  return (
    <div
      className="modal-backdrop-custom"
      onClick={closeModal}
    >
      <div
        className="modal-card-custom"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-header-custom">
          <div>
            <h2>Unlock client details</h2>

            <p>
              Enter your 4-digit privacy PIN.
              Client details will remain unlocked for 1 hour.
            </p>
          </div>

          <button
            type="button"
            className="modal-close-btn"
            onClick={closeModal}
            disabled={isLoading || isSendingReset}
            aria-label="Close"
          >
            <X />
          </button>
        </div>

        {error && (
          <div className="alert alert-danger">
            {error}
          </div>
        )}

        {message && (
          <div className="alert alert-success">
            {message}
          </div>
        )}

        {lockedOut && (
          <div className="alert alert-warning">
            Too many incorrect PIN attempts.
            Please try again later.
          </div>
        )}

        {!confirmReset ? (
          <>
            <div>
              <label
                htmlFor="privacy-pin-input"
                className="form-label"
              >
                Privacy PIN
              </label>

              <input
                id="privacy-pin-input"
                type={showPin ? "text" : "password"}
                className="modal-input"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                autoComplete="off"
                value={pin}
                onChange={handlePinChange}
                placeholder="••••"
                autoFocus
                disabled={isLoading || lockedOut}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    unlock();
                  }
                }}
              />

              <button
                type="button"
                className="btn btn-link p-0 mt-2"
                onClick={() => {
                  setError("");
                  setMessage("");
                  setConfirmReset(true);
                }}
                disabled={isLoading || isSendingReset}
              >
                Forgot PIN?
              </button>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="modal-cancel-btn"
                onClick={() =>
                  setShowPin((current) => !current)
                }
                disabled={isLoading || lockedOut}
              >
                {showPin ? (
                  <>
                    <EyeSlash className="me-2" />
                    Hide PIN
                  </>
                ) : (
                  <>
                    <Eye className="me-2" />
                    Show PIN
                  </>
                )}
              </button>

              <button
                type="button"
                className="modal-cancel-btn"
                onClick={closeModal}
                disabled={isLoading || isSendingReset}
              >
                Cancel
              </button>

              <button
                type="button"
                className="modal-save-btn"
                onClick={unlock}
                disabled={
                  isLoading ||
                  lockedOut ||
                  pin.length !== 4
                }
              >
                {isLoading
                  ? "Unlocking..."
                  : "Unlock details"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="alert alert-warning">
              <strong>Forgot your PIN?</strong>

              <p className="mb-0 mt-2">
                We can send a secure PIN reset link to the
                email address registered to your account.
              </p>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="modal-cancel-btn"
                onClick={() => setConfirmReset(false)}
                disabled={isSendingReset}
              >
                Cancel
              </button>

              <button
                type="button"
                className="modal-save-btn"
                onClick={sendResetEmail}
                disabled={isSendingReset}
              >
                {isSendingReset
                  ? "Sending..."
                  : "Send reset link"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}